import {
  Block,
  BlockMilestone,
  BlockRelationship,
  InstanceBlock,
  blockMilestoneBaseOps,
  isBlockWithChildren,
  isBranch,
  isDocumentBlock,
  isListBlock,
  isRootBlock,
} from '@basehub/data/replicache/index'
import { getMilestoneId } from '@basehub/data/replicache/schema/block-milestone/util'
import { invariant } from '@epic-web/invariant'
import {
  blockBaseOps,
  type BaseHubTransaction,
} from '@basehub/data/replicache/tx/iso'
import { areBlocksCompatible } from '@basehub/data/replicache/schema/block/compare'
import { idToSourceBlockId } from '../mutators/copy-on-write'
import { Entry, schema } from '@basehub/data/db/index'
import { createIndexedMap } from '@basehub/utils/indexed-map/index'
import { sql } from 'drizzle-orm'
import { getNewBlockDataByType } from '@basehub/data/replicache/helpers/get-new-block-data-by-type'

const DEBUG = false
const logger: typeof console.log = (...args) => {
  // eslint-disable-next-line no-console
  if (DEBUG) console.log(...args)
}

export type RevalidateBlockMilestoneParams = {
  isPropagating: string | null
  path: string | null
  sourceBlockId: string
  rootId: string
  isoNow: string
  blockId: string
}

export const revalidateBlockMilestone = async (
  tx: BaseHubTransaction,
  {
    path,
    isPropagating,
    sourceBlockId,
    rootId,
    isoNow,
    blockId,
  }: RevalidateBlockMilestoneParams
) => {
  if (!tx.serverOnly?.session) return
  const userId = tx.serverOnly.session?.userId
  invariant(userId, 'userId is required')
  const branchOrCommit = tx.serverOnly.resolvedRef?.value
  invariant(
    branchOrCommit && isBranch(branchOrCommit),
    'branchOrCommit must be a branch, got: ' + JSON.stringify(branchOrCommit)
  )

  const headCommit = branchOrCommit.headCommit
  const headCommitTreeManager = headCommit
    ? await tx.getTreeManager(headCommit.rootBlockId, {
        ignoreTxCache: true,
        ignoreCopyOnWrite: true,
      })
    : null

  // Prepare block entries to compare and figure the current status out
  let sourceBlock: Block | null = null
  let sourceBrOrder: string | null = null
  let sourceParentBlock: Block | null = null

  // 1.1 If there's a head commit, fetch previous state: block, parent, br and children (also brs)
  if (headCommit && headCommitTreeManager) {
    const sourceNode = headCommitTreeManager.getNode(sourceBlockId)
    sourceBlock = sourceNode?.value ?? null
    sourceBrOrder = sourceNode?.order ?? null
    sourceParentBlock = sourceNode?.parentKey
      ? (headCommitTreeManager.getNode(sourceNode.parentKey)?.value ?? null)
      : null
  }

  // 1.2 Fetch current state: block, parent, br and children (also brs)
  const [currentBlock, currentBr, children] = await Promise.all([
    blockBaseOps.get(tx, blockId, { acceptDeleted: true }),
    tx.getBlockRelationshipByBlockId({
      blockId,
      rootBlockId: rootId,
    }),
    tx.getBlockChildren({
      sourceBlockId,
      rootBlockId: rootId,
    }),
  ])

  const parentBlock = currentBr
    ? await blockBaseOps.get(tx, currentBr.parentId)
    : null
  // 1.3 Index source children for easier comparison
  const indexedChildrenBRs = createIndexedMap<BlockRelationship>({
    autoFallbackKeys: ['blockId', 'sourceBlockId'],
  })

  // 2. Get existing milestone, and start comparing to get status, contributors, reordered and dependencies
  const milestoneId = getMilestoneId({ sourceBlockId, rootId })
  let existing = (await blockMilestoneBaseOps.get(tx, milestoneId)) ?? null

  invariant(
    typeof existing?.commitId !== 'string',
    'Cannot modify a committed milestone'
  )

  if (existing?.deletedAt) {
    existing = null // don't re-use deleted milestones
  }

  // 2.1 Calculate reorder
  const isBeingReordered =
    parentBlock &&
    sourceParentBlock &&
    idToSourceBlockId(parentBlock) !== idToSourceBlockId(sourceParentBlock)

  let reordered = existing?.reordered
  if (isBeingReordered) {
    reordered = {
      from: idToSourceBlockId(sourceParentBlock!),
      to: idToSourceBlockId(parentBlock),
    }

    logger('MILESTONE REORDERED:', reordered)
  }

  // 2.2 Calculate status
  const dependenciesSet = new Set(existing?.dependencies ?? [])

  const status = (() => {
    if (!sourceBlock || sourceBlock.deletedAt) {
      if (!currentBlock || currentBlock.deletedAt) return 'unmodified'

      if (
        existing?.contributors.created?.[0] &&
        existing?.contributors.created[0] !== userId
      ) {
        return 'modified'
      }
      return 'created'
    }
    if (!currentBlock || currentBlock.deletedAt) return 'deleted'
    if (isPropagating && !dependenciesSet.has(isPropagating)) return 'modified'
    if (isBeingReordered) return 'modified'
    if (
      !areBlocksCompatible(sourceBlock, currentBlock) ||
      sourceBrOrder !== currentBr?.order
    ) {
      return 'modified'
    }
    if (children.length !== indexedChildrenBRs.size()) {
      return 'modified'
    }
    for (const br of children) {
      const indexedBr = indexedChildrenBRs.get(br.id)
      if (!indexedBr) return 'modified'
    }
    return 'unmodified'
  })()

  let indexingPromise: Promise<unknown> | null = null
  const repoId = tx.serverOnly.repo?.id
  invariant(repoId, 'repoId is required')

  if (status === 'unmodified') {
    logger('unmodified milestone', milestoneId)
    if (currentBr && currentBlock) {
      // indexingPromise = index({
      //   items: [{ br: currentBr, block: currentBlock }],
      //   repoId,
      //   rootBlock,
      // })
      indexingPromise = Promise.resolve()
    }
    return await Promise.all([
      updateOrDeleteBlockMilestone(tx, {
        id: milestoneId,
        isoNow,
        removeDependency: milestoneId,
      }),
      indexingPromise,
    ])
  }

  // 2.3 Calculate contributors
  const contributors = existing?.contributors ?? {}

  const isAddingNewUser =
    existing && !Object.values(contributors).flat().includes(userId)

  switch (status) {
    case 'created':
      contributors[status] = [userId]
      break
    case 'modified':
      // ignore array update if the user is the creator
      if (contributors.created?.[0] === userId) break

      contributors[status] = Array.from(
        new Set([...(contributors[status] ?? []), userId])
      )
      break
    case 'deleted':
      contributors[status] = [userId]
      break
  }

  async function updateDependantOnPath(
    path: string,
    updateFn: (args: {
      blockId: string
      sourceBlockId: string
    }) => Promise<unknown>
  ) {
    if (path === '') return
    const promises: Promise<unknown>[] = []

    const blocks = await Promise.all(
      path
        .split('/')
        .reverse()
        .map(async (id) => ({
          id,
          segmentParent: await blockBaseOps.get(tx, id),
        }))
    )

    for (let { id, segmentParent } of blocks) {
      if (!segmentParent) {
        // virtual block
        segmentParent = {
          ...getNewBlockDataByType({
            type: 'instance',
            id,
            sourceBlockId: id,
            typeOptions: {
              targetBlockId: '',
            },
          }),
          __virtual: true,
        } as InstanceBlock & { __virtual: true }
      }

      if (isRootBlock(segmentParent)) break
      if (segmentParent.id === blockId) continue
      if (isListBlock(segmentParent)) break

      promises.push(
        updateFn({
          blockId: segmentParent.id,
          sourceBlockId: idToSourceBlockId(segmentParent),
        })
      )
      if (isDocumentBlock(segmentParent)) break
    }

    await Promise.all(promises)
  }

  // 2.4 Calculate dependencies
  // if not propagating, it means that the update comes from the block itself, so we add it to the dependency list
  dependenciesSet.add(isPropagating ?? milestoneId)

  logger({ isBeingReordered, status, isAddingNewUser, isNew: !existing })
  if (isBeingReordered || !existing || isAddingNewUser) {
    if (existing?.reordered && existing.reordered.to !== reordered?.to) {
      const treeManager = await tx.getTreeManager(rootId)
      const previousParentPath = treeManager.getPath(existing.reordered.to, {
        ignoreSelf: false,
        required: true,
        ignoreRoot: true,
      })

      await updateDependantOnPath(previousParentPath, ({ sourceBlockId }) =>
        updateOrDeleteBlockMilestone(tx, {
          isoNow,
          removeDependency: milestoneId,
          id: getMilestoneId({
            rootId,
            sourceBlockId,
          }),
        })
      )
    }

    if (path) {
      await updateDependantOnPath(path, ({ blockId, sourceBlockId }) =>
        revalidateBlockMilestone(tx, {
          isPropagating: milestoneId,
          path: null,
          isoNow,
          blockId,
          sourceBlockId,
          rootId,
        })
      )
    }
  }

  if ((status === 'deleted' || !isBeingReordered) && existing?.reordered) {
    if (
      // if the block was reorder to the root, then this will be a no-op, so we save the trouble
      existing.reordered.to !== rootId
    ) {
      const treeManager = await tx.getTreeManager(rootId)
      const previousParentPath = treeManager.getPath(existing.reordered.to, {
        ignoreSelf: false,
        required: true,
        ignoreRoot: true,
      })

      await updateDependantOnPath(previousParentPath, ({ sourceBlockId }) =>
        updateOrDeleteBlockMilestone(tx, {
          isoNow,
          removeDependency: milestoneId,
          id: getMilestoneId({
            rootId,
            sourceBlockId,
          }),
        })
      )
    }
  }

  const block = currentBlock ?? sourceBlock
  invariant(block, 'block must exist')

  // 3. Create or update milestone
  const newMilestone: BlockMilestone = {
    createdAt: isoNow,
    updatedAt: isoNow,
    id: milestoneId,
    branchId: branchOrCommit.id,
    ...existing,
    currentStatus: status,
    sourceBlockId,
    rootId,
    blockId,
    parentId:
      !isBlockWithChildren(block) && parentBlock
        ? idToSourceBlockId(parentBlock)
        : null,
    contributors,
    reordered,
    deletedAt: null,
    dependencies: Array.from(dependenciesSet),
  }
  delete newMilestone.commitId

  logger('Before upsert', newMilestone)
  await blockMilestoneBaseOps.set(tx, newMilestone)
  return newMilestone
}

const updateOrDeleteBlockMilestone = async (
  tx: BaseHubTransaction,
  {
    id,
    isoNow,
    removeDependency,
  }: {
    id: string
    isoNow: string
    removeDependency: string
  }
) => {
  if (!tx.serverOnly) return
  const milestone = await blockMilestoneBaseOps.get(tx, id)
  if (!milestone) return

  const filteredDependencies = milestone.dependencies.filter(
    (dep) => dep !== removeDependency
  )

  if (filteredDependencies.length === 0) {
    logger('Will delete milestone:', id)
    await deleteBlockMilestoneAndItsDependencies_DO_NOT_USE_DIRECTLY(tx, {
      id,
      isoNow,
    })
    return
  }

  await blockMilestoneBaseOps.update(tx, {
    id,
    dependencies: filteredDependencies,
  })
}

/**
 * Adds deletedAt for the provided id and looks for all dependant milestones in order to remove the if from their array.
 * @private **This function should not be used directly, use `updateOrDeleteBlockMilestone` instead.**
 */
const deleteBlockMilestoneAndItsDependencies_DO_NOT_USE_DIRECTLY = async (
  tx: BaseHubTransaction,
  {
    id: idToRemove,
    isoNow,
  }: {
    id: string
    isoNow: string
  }
) => {
  if (!tx.serverOnly) return
  const key = `milestone/${idToRemove}`
  const dependableMilestones = await tx.serverOnly.executor.execute<
    Entry<BlockMilestone>
  >(sql`
      SELECT value FROM ${schema.entry}
      WHERE key LIKE 'milestone/%'
      AND key != ${key}
      AND spaceid = ${tx.serverOnly.spaceID}
      AND value->'dependencies' @> ${`["${idToRemove}"]`}
      AND value->>'deletedAt' IS NULL
      AND value->>'commitId' IS NULL
      `)

  logger(
    'Will update deps for:',
    JSON.stringify(
      dependableMilestones.map((m) => m.value.blockId),
      null,
      2
    )
  )

  const promises = dependableMilestones.map((entry) =>
    updateOrDeleteBlockMilestone(tx, {
      id: entry.value.id,
      isoNow,
      removeDependency: idToRemove,
    })
  )

  await Promise.all([
    ...promises,
    blockMilestoneBaseOps.update(tx, { id: idToRemove, deletedAt: isoNow }),
  ])
}
